import { Grid } from "@material-ui/core"
import { TextButton } from "@tfo/common"
import { IOrderClient, ReportPackage } from "@tfo/common/src/models"
import React, { useEffect, useState } from "react"
import { IntlShape, useIntl } from "react-intl"
import $http from "../../../../../../../utils/$http"
import { displayNationalId } from "../../../../../../../utils/NationalId"
import CustomerPortalDialog from "../../../../../common/CustomerPortalDialog"
import DepersonalizeDialog from "./DepersonalizeDialog"

import "./ReportsItemOngoingOrderDialog.scss"

type FoundPartnerAddonJobsSchema = {
    jobs: Array<{
        id: number
        addonId: number
        partnerId: number
        partnerName: string
        addonName: string
        updates: Array<{
            id: number
            timestamp: string
            state: string
            description: string
        }>
    }>
}

type Addon = {
    id: number
    name: string
    updates: Array<{
        id: number
        timestamp: Date
        state: string
        description: string
    }>
}

type PartnerAddons = {
    name: string
    addons: Array<Addon>
}

type Creator = {
    firstName: string
    lastName: string
}

const ReportsItemOngoingOrderDialog = ({ open, onClose, selectedOrder, reportPackages, removeOrder }:
    {
        open: boolean
        onClose: (order?: IOrderClient) => void
        selectedOrder: any
        reportPackages: Array<ReportPackage>
        removeOrder: (order: IOrderClient) => void
    }) => {
    const intl = useIntl()
    const [creator, setCreator] = useState<Creator | null>(null)
    const [cancelOngoingOrderModal, setCancelOngoingOrderModal] = useState<boolean>(false)
    const [addons, setAddons] = useState<PartnerAddons[]>([])
    const [individualReference, setIndividualReference] = useState<string | null>(null)

    useEffect(() => {
        $http.get<Creator>(`/user/customer/${selectedOrder.creatorId}`).then(resp => setCreator(resp))
        $http.get<string>(`/order/${selectedOrder.id}/individual-reference`).then(resp => setIndividualReference(resp))
        $http.get<FoundPartnerAddonJobsSchema>(`/partneraddons/jobs/${selectedOrder.id}`).then(resp => {
            const groupedAddons = resp.jobs.reduce((groups, job) => {
                const displayPartnerName = job.partnerName.toLowerCase().startsWith("ljung & sjöberg") ? "L&S" : job.partnerName
                const addon: Addon = {
                    id: job.addonId,
                    name: job.addonName,
                    updates: job.updates
                        .map(update => ({ ...update, timestamp: new Date(update.timestamp) }))
                        .toSorted((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
                };

                const group = groups.find(g => g.name === displayPartnerName)
                if (group) {
                    group.addons.push(addon)
                    return groups;
                }
                else {
                    return [...groups, { name: displayPartnerName, addons: [addon] } as PartnerAddons];
                }
            }, [] as PartnerAddons[]);

            groupedAddons.sort((a, b) => a.name.localeCompare(b.name))
            setAddons(groupedAddons)
        })
    }, [selectedOrder])

    const close = (order?: IOrderClient) => {
        setCancelOngoingOrderModal(false);
        onClose(order);
    }

    const hasCountries = (countries: Array<string>): boolean => {
        if (!Array.isArray(countries) || !countries.length) {
            return false
        }
        return true;
    }

    return (
        <>
            {
                <CustomerPortalDialog
                    open={open}
                    onClose={() => close()}
                    title={intl.formatMessage({ id: 'reportsitem.ongoing.dialog.title' })}
                    content={
                        <Grid container spacing={1} className="ReportsItemOngoingOrderDialog">
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.customer' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {selectedOrder?.companyName}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.reportPackage' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {reportPackages.find(x => x.id === selectedOrder?.servicePackageId)?.customName || ''}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.individualName' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {`${selectedOrder?.candidate?.firstName} ${selectedOrder?.candidate?.lastName}`}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({
                                        id: selectedOrder?.candidate?.nationalId.country === "Other"
                                            ? 'reportsitem.ongoing.dialog.birthdate'
                                            : 'reportsitem.ongoing.dialog.nationalId'
                                    })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {displayNationalId(selectedOrder?.candidate, false)}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.individual' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {selectedOrder?.controlPerson}
                            </Grid>
                            {
                                individualReference &&
                                <>
                                    <Grid item xs={4}>
                                        <b>
                                            {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.individualreference' })}
                                        </b>
                                    </Grid>
                                    <Grid item xs={8}>
                                        {individualReference}
                                    </Grid>
                                </>
                            }
                            {
                                hasCountries(selectedOrder?.targetCountries) &&
                                <>
                                    <Grid item xs={4}>
                                        <b>
                                            {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.countries' })}
                                        </b>
                                    </Grid>
                                    <Grid item xs={8}>
                                        {selectedOrder?.targetCountries.join(', ')}
                                    </Grid>
                                </>
                            }
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.orderer' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {`${creator?.firstName} ${creator?.lastName}`}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.orderReceiver' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {selectedOrder?.orderRecipients.map(x => x.userNameAndSurName).join(', ')}
                            </Grid>
                            <Grid item xs={4}>
                                <b>
                                    {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.deliveryDate' })}
                                </b>
                            </Grid>
                            <Grid item xs={8}>
                                {intl.formatDate(selectedOrder?.availableFrom, {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                })}
                            </Grid>
														<Grid item xs={4}>
															<b>
																{intl.formatMessage({id: 'reportsitem.ongoing.dialog.noteToTfo'})}
															</b>
														</Grid>
														<Grid item xs={8}>
															{selectedOrder?.customerComment}
														</Grid>
														<Grid item xs={4}>
															<b>
																{intl.formatMessage({id: 'reportsitem.ongoing.dialog.customerInternalComment'})}
															</b>
														</Grid>
														<Grid item xs={8}>
															{selectedOrder?.customerInternalComment}
														</Grid>
                            {addons.length > 0 && <>
                                <Grid item xs={4}>
                                    <b>
                                        {intl.formatMessage({ id: 'reportsitem.ongoing.dialog.addons' })}
                                    </b>
                                </Grid>

                                <Grid item xs={8}>
                                    {addons.map(p => p.addons.map(a => <>
                                        <div key={a.id}>{p.name}</div>
                                        {renderAddonUpdates(p.name, a, intl)}
                                    </>))}
                                </Grid>
                            </>}
                        </Grid>
                    }
                    footer={
                        <Grid container spacing={2} justifyContent="center">
                            <TextButton grey={true} onClick={() => setCancelOngoingOrderModal(true)}>{intl.formatMessage({ id: 'reportsitem.ongoing.dialog.cancelorder' })}</TextButton>
                        </ Grid>
                    } />
            }

            <DepersonalizeDialog openModal={cancelOngoingOrderModal} dialogTitle={intl.formatMessage({ id: 'reportsitem.individual.depersonalize.dialog.title.postApproved' }, { candidateName: selectedOrder?.candidate?.firstName + ' ' + selectedOrder?.candidate?.lastName })} dialogBody="reportsitem.ongoing.dialog.cancelorder.bodytext" closeModal={() => setCancelOngoingOrderModal(false)} orderId={selectedOrder.id} individual={selectedOrder.candidate} depersonalizationCompleted={() => removeOrder(selectedOrder)} />
        </>
    )
}

type GroupedUpdates = { 
    [state: string]: Addon['updates']
}
const renderAddonUpdates = (partnerName: string, a: Addon, intl: IntlShape): React.ReactNode => {
    // Remove duplicate descriptions, keep the first occurence
    let lastDescription = ""
    const updatesWithScrubbedDescriptions = a.updates
    .map(update => {
        const currentDescription = update.description && update.description !== lastDescription 
            ? update.description 
            : null

        if(currentDescription) {
            lastDescription = update.description
        }
        return {
            ...update,
            description: currentDescription
        }
    })

    // Group by state, as some addons might send several updates for the same state, updating description
    // result-delivered is excluded as customers should not see the result on ongoing orders
    const groupedUpdates = updatesWithScrubbedDescriptions.reduce((acc, update) => {
        if (update.state === 'result-delivered') return acc
        if (!acc[update.state]) {
            acc[update.state] = []
        }
        acc[update.state].push(update)
        return acc
    }, {} as GroupedUpdates)

    // Pick the first state occurence, but keep the latest description (if any), and sort by timestamp
    const normalizedUpdates = Object
        .values(groupedUpdates)
        .map(updates => { 
            const latestDescription = updates.reduce((latest, update) => update.description ? update.description : latest, "")
           
            return {
                ...updates[0],
                description: latestDescription
            }
        })
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

    const latestUpdate = normalizedUpdates[normalizedUpdates.length - 1]
    return <ul key={`updates-${a.id}`} className="orderAddons">
        {normalizedUpdates.map(u =>
            <li key={u.id}>
                {`${intl.formatDate(u.timestamp)} - ${intl.formatMessage({ id: `reportsitem.ongoing.dialog.addons.state.${u.state}` })}`}
            </li>)}
        {latestUpdate.description &&
            <li key="partner-notes" className="partner-notes">
                <div>{intl.formatMessage({ id: 'reportsitem.ongoing.dialog.addons.description.disclaimer' }, { partnerName })}</div>
                <div>{latestUpdate.description}</div>
            </li>}
    </ul>
}


export default ReportsItemOngoingOrderDialog
